import { Link } from 'react-router-dom';

/**
 * "What are you replacing?" — the productized opening catalog.
 *
 * Phase 8 refinement: more architectural, warmer.
 *  - Cards on warm stone, hairline border, no shadow at rest, micro-lift on hover.
 *  - Illustrations sit directly on the stone surface (no inner panel).
 *  - SVG stroke recolored to warm charcoal (was cold ocean blue).
 *  - Headlines in display serif. CTA text + champagne arrow (not button chip).
 *
 * Each card routes to /get-quote with `?src=catalog_<id>` for attribution.
 */

const STROKE = '#1A1F26';          // warm charcoal (was cold ocean blue)
const GLASS = '#EAEFF3';           // softer, less saturated glass tint
const HANDLE = '#A88654';          // champagne accents on handles/arrows
const FRAME_FILL = '#FFFFFF';

function SingleHungIllustration() {
  return (
    <svg viewBox="0 0 96 80" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Single hung window">
      <rect x="18" y="8" width="60" height="64" rx="2" fill={FRAME_FILL} stroke={STROKE} strokeWidth="1.25" />
      <rect x="22" y="12" width="52" height="28" rx="1" fill={GLASS} stroke={STROKE} strokeWidth="0.75" />
      <rect x="22" y="40" width="52" height="28" rx="1" fill={GLASS} stroke={STROKE} strokeWidth="0.75" />
      <line x1="22" y1="40" x2="74" y2="40" stroke={STROKE} strokeWidth="1.25" />
      <rect x="42" y="61" width="12" height="2.5" rx="1.25" fill={HANDLE} />
      <path d="M 84 30 L 84 50" stroke={HANDLE} strokeWidth="1" strokeLinecap="round" />
      <path d="M 81 47 L 84 50 L 87 47" stroke={HANDLE} strokeWidth="1" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function HorizontalRollerIllustration() {
  return (
    <svg viewBox="0 0 96 80" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Horizontal roller window">
      <rect x="10" y="16" width="76" height="48" rx="2" fill={FRAME_FILL} stroke={STROKE} strokeWidth="1.25" />
      <rect x="14" y="20" width="32" height="40" rx="1" fill={GLASS} stroke={STROKE} strokeWidth="0.75" />
      <rect x="50" y="20" width="32" height="40" rx="1" fill={GLASS} stroke={STROKE} strokeWidth="0.75" />
      <line x1="48" y1="20" x2="48" y2="60" stroke={STROKE} strokeWidth="1.25" />
      <rect x="42" y="38" width="2.5" height="12" rx="1.25" fill={HANDLE} />
      <path d="M 36 70 L 60 70" stroke={HANDLE} strokeWidth="1" strokeLinecap="round" />
      <path d="M 57 67 L 60 70 L 57 73" stroke={HANDLE} strokeWidth="1" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FixedPictureIllustration() {
  return (
    <svg viewBox="0 0 96 80" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Fixed picture window">
      <rect x="10" y="10" width="76" height="60" rx="2" fill={FRAME_FILL} stroke={STROKE} strokeWidth="1.25" />
      <rect x="14" y="14" width="68" height="52" rx="1" fill={GLASS} stroke={STROKE} strokeWidth="0.75" />
      <path d="M 22 18 L 28 24 L 22 30 Z" fill="#FFFFFF" opacity="0.6" />
      <path d="M 30 18 L 36 24 L 30 30 Z" fill="#FFFFFF" opacity="0.35" />
    </svg>
  );
}

function SlidingGlassDoorIllustration() {
  return (
    <svg viewBox="0 0 96 80" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Sliding glass door">
      <rect x="14" y="4" width="68" height="72" rx="1.5" fill={FRAME_FILL} stroke={STROKE} strokeWidth="1.25" />
      <rect x="18" y="8" width="30" height="60" rx="0.75" fill={GLASS} stroke={STROKE} strokeWidth="0.75" />
      <rect x="48" y="8" width="30" height="60" rx="0.75" fill={GLASS} stroke={STROKE} strokeWidth="0.75" />
      <line x1="48" y1="8" x2="48" y2="68" stroke={STROKE} strokeWidth="1.25" />
      <rect x="43" y="34" width="2.5" height="12" rx="1.25" fill={HANDLE} />
      <rect x="50" y="34" width="2.5" height="12" rx="1.25" fill={HANDLE} />
      <line x1="18" y1="72" x2="78" y2="72" stroke={STROKE} strokeWidth="1.25" />
      <path d="M 56 76 L 76 76" stroke={HANDLE} strokeWidth="1" strokeLinecap="round" />
      <path d="M 73 73 L 76 76 L 73 79" stroke={HANDLE} strokeWidth="1" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FrenchDoorIllustration() {
  return (
    <svg viewBox="0 0 96 80" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="French door">
      <rect x="16" y="4" width="64" height="72" rx="1.5" fill={FRAME_FILL} stroke={STROKE} strokeWidth="1.25" />
      <rect x="20" y="8" width="26" height="64" rx="0.75" fill={GLASS} stroke={STROKE} strokeWidth="0.75" />
      <rect x="50" y="8" width="26" height="64" rx="0.75" fill={GLASS} stroke={STROKE} strokeWidth="0.75" />
      <line x1="20" y1="30" x2="46" y2="30" stroke={STROKE} strokeWidth="0.5" />
      <line x1="20" y1="50" x2="46" y2="50" stroke={STROKE} strokeWidth="0.5" />
      <line x1="50" y1="30" x2="76" y2="30" stroke={STROKE} strokeWidth="0.5" />
      <line x1="50" y1="50" x2="76" y2="50" stroke={STROKE} strokeWidth="0.5" />
      <line x1="48" y1="8" x2="48" y2="72" stroke={STROKE} strokeWidth="0.75" />
      <circle cx="42" cy="40" r="1.5" fill={HANDLE} />
      <circle cx="54" cy="40" r="1.5" fill={HANDLE} />
    </svg>
  );
}

function FrontEntryDoorIllustration() {
  return (
    <svg viewBox="0 0 96 80" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Front entry door">
      <rect x="22" y="4" width="52" height="72" rx="1.5" fill={FRAME_FILL} stroke={STROKE} strokeWidth="1.25" />
      <rect x="26" y="8" width="44" height="64" rx="0.75" fill="#F2EEE7" stroke={STROKE} strokeWidth="0.75" />
      <rect x="32" y="14" width="32" height="18" rx="0.75" fill={GLASS} stroke={STROKE} strokeWidth="0.5" />
      <rect x="32" y="38" width="32" height="14" rx="0.75" fill="none" stroke={STROKE} strokeWidth="0.5" />
      <rect x="32" y="56" width="32" height="12" rx="0.75" fill="none" stroke={STROKE} strokeWidth="0.5" />
      <circle cx="63" cy="45" r="1.5" fill={HANDLE} />
    </svg>
  );
}

const OPENINGS = [
  {
    id: 'single-hung',
    name: 'Single Hung Windows',
    desc:
      'Common in bedrooms, bathrooms, and living areas. Opens vertically and is one of the most common South Florida replacement windows.',
    Illustration: SingleHungIllustration,
  },
  {
    id: 'horizontal-roller',
    name: 'Horizontal Roller Windows',
    desc:
      'Slides left to right and works well for wider openings, kitchens, and rooms where vertical operation is not preferred.',
    Illustration: HorizontalRollerIllustration,
  },
  {
    id: 'fixed-picture',
    name: 'Fixed / Picture Windows',
    desc:
      'Does not open. Used when you want light, views, and impact protection without ventilation.',
    Illustration: FixedPictureIllustration,
  },
  {
    id: 'sliding-glass-door',
    name: 'Sliding Glass Doors',
    desc:
      'Common for patios, balconies, and backyard access. One of the biggest pricing factors because of size and glass area.',
    Illustration: SlidingGlassDoorIllustration,
  },
  {
    id: 'french-door',
    name: 'French Doors',
    desc:
      'A classic hinged door option for patios, side entries, and exterior access points.',
    Illustration: FrenchDoorIllustration,
  },
  {
    id: 'front-entry-door',
    name: 'Front Entry Doors',
    desc:
      'Impact-rated entry doors for front entrances, side doors, and garage access doors.',
    Illustration: FrontEntryDoorIllustration,
  },
];

const trackCardClick = (id) => {
  if (typeof window === 'undefined') return;
  try {
    if (window.gtag) {
      window.gtag('event', 'opening_catalog_click', {
        event_category: 'catalog',
        opening_id: id,
      });
    }
    if (window.fbq) {
      window.fbq('trackCustom', 'CatalogClick', { opening_id: id });
    }
  } catch (_) { /* ignore */ }
};

export default function OpeningCatalog({
  src = 'homepage_catalog',
  variant = 'default', // 'default' | 'compact'
  showHeadline = true,
  className = '',
}) {
  const compact = variant === 'compact';
  return (
    <section
      className={`${compact ? 'py-16 sm:py-20' : 'py-20 sm:py-28'} bg-white ${className}`}
      data-testid="opening-catalog"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        {showHeadline && (
          <div className="max-w-2xl mb-12 sm:mb-16">
            <p className="text-[11px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-[var(--snap-champagne-600)] mb-4">
              What are you replacing?
            </p>
            <h2
              className="font-heading text-[34px] sm:text-[44px] md:text-[52px] leading-[1.05] text-[var(--snap-charcoal)]"
              data-testid="opening-catalog-headline"
            >
              Choose the openings you want quoted.
            </h2>
            <p className="mt-5 text-[15px] sm:text-base text-[var(--snap-slate-500)] max-w-xl leading-relaxed">
              Pick any combination. Upload one photo per opening when you start your quote
              — we’ll handle the rest.
            </p>
          </div>
        )}

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
          data-testid="opening-catalog-grid"
        >
          {OPENINGS.map(({ id, name, desc, Illustration }) => (
            <Link
              key={id}
              to={`/get-quote?src=${encodeURIComponent(`catalog_${id}`)}`}
              onClick={() => trackCardClick(id)}
              className="group block"
              data-testid={`opening-card-${id}`}
              aria-label={`Get my price for ${name}`}
            >
              <article className="h-full rounded-xl border border-[var(--snap-stone-200)] bg-[var(--snap-stone-50)] transition-all duration-200 group-hover:border-[var(--snap-stone-300)] group-hover:-translate-y-[1px]">
                <div className="px-6 pt-8 pb-5 flex items-center justify-center aspect-[6/5]">
                  <div className="w-[72%] h-[72%] flex items-center justify-center">
                    <Illustration />
                  </div>
                </div>
                <div className="px-6 pb-7">
                  <h3 className="font-heading text-[22px] sm:text-[24px] leading-snug text-[var(--snap-charcoal)] mb-2">
                    {name}
                  </h3>
                  <p className="text-[14.5px] sm:text-[15px] text-[var(--snap-slate-500)] leading-relaxed mb-5">
                    {desc}
                  </p>
                  <div
                    className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[var(--snap-charcoal)] group-hover:text-[var(--snap-navy-900)] transition-colors"
                    data-testid={`opening-card-cta-${id}`}
                  >
                    Get My Price
                    <span
                      className="text-[var(--snap-champagne-600)] text-base leading-none transition-transform group-hover:translate-x-0.5"
                      aria-hidden="true"
                    >
                      →
                    </span>
                  </div>
                </div>
              </article>
            </Link>
          ))}
        </div>

        <p className="mt-12 sm:mt-14 text-[13px] sm:text-sm italic text-[var(--snap-slate-500)] text-center">
          Not sure which type? Just upload a photo — we’ll identify it for you.
        </p>
      </div>
    </section>
  );
}
